import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { authClient, type AuthSession } from '@/services/api';
import { notifications } from '@mantine/notifications';

export function useAuth() {
  const queryClient = useQueryClient();

  // Fetch session
  const {
    data: session,
    isLoading,
    error,
  } = useQuery<AuthSession>({
    queryKey: ['session'],
    queryFn: authClient.getSession,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false,
  });

  // Sign out mutation
  const signOutMutation = useMutation({
    mutationFn: authClient.signOut,
    onSuccess: () => {
      // Invalidate session query
      queryClient.setQueryData(['session'], { user: null, session: null });
      notifications.show({
        title: 'Signed out',
        message: 'You have been successfully signed out',
        color: 'blue',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Sign out failed',
        message: error.message,
        color: 'red',
      });
    },
  });

  return {
    user: session?.user || null,
    session: session?.session || null,
    isLoading,
    error,
    isAuthenticated: !!session?.user,
    signIn: authClient.signInWithGitHub,
    signOut: () => signOutMutation.mutate(),
    isSigningOut: signOutMutation.isPending,
  };
}
